import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiError, hasSession, onSessionChange, signOut } from './data/api';
import { getProfile, type Profile } from './data/agency';
import { errorText } from './data/words';
import { ProfileContext } from './shell/profile';
import { Register } from './shell/Register';
import { AppShell } from './shell/AppShell';
import { SignIn, resetTokenInUrl } from './shell/SignIn';
import { Btn, Empty, Note } from './ui';
import { OverviewScreen } from './screens/overview';
import { RequestsScreen } from './screens/requests';
import { AssignScreen } from './screens/assign';
import { LiveScreen } from './screens/live';
import { TeamScreen } from './screens/team';
import { CoverageScreen } from './screens/coverage';
import { PricesScreen } from './screens/prices';
import { DocumentsScreen } from './screens/documents';
import { EarningsScreen } from './screens/earnings';
import { ProfileScreen } from './screens/profile';

// The rail's counts drive the new-request chime (shell/Alerts.tsx), and the
// first agency to accept wins: fifteen seconds, as the Requests page polls.
const PROFILE_POLL_MS = 15_000;

export default function App() {
  const [signedIn, setSignedIn] = useState(hasSession());
  const [registering, setRegistering] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => onSessionChange(() => setSignedIn(hasSession())), []);

  const reload = useCallback(async () => {
    if (!hasSession()) return;
    try {
      setProfile(await getProfile());
      setError(null);
    } catch (e) {
      // A signed-in phone that belongs to no agency is signed out with the
      // reason, not left on a broken page.
      if (e instanceof ApiError && (e.code === 'AGENCY_SESSION_REQUIRED' || e.code === 'NO_AGENCY_MEMBERSHIP')) {
        setNotice(e.message);
        signOut();
        return;
      }
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    if (!signedIn) {
      setProfile(null);
      return;
    }
    setNotice(null);
    setRegistering(false);
    void reload();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, PROFILE_POLL_MS);
    return () => window.clearInterval(t);
  }, [signedIn, reload]);

  // A "set a new password" link opens the reset page even in a browser that
  // is still signed in: the reset signs every session out anyway.
  if (!signedIn || resetTokenInUrl()) {
    return registering ? <Register onBack={() => setRegistering(false)} /> : <SignIn onRegister={() => setRegistering(true)} notice={notice} />;
  }

  if (!profile) {
    return (
      <div style={{ padding: 32, maxWidth: 560, display: 'grid', gap: 12 }}>
        {error ? (
          <>
            <Note tone="danger">{error}</Note>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn onClick={() => void reload()}>Try again</Btn>
              <Btn onClick={signOut}>Sign out</Btn>
            </div>
          </>
        ) : (
          <Empty>Loading your agency…</Empty>
        )}
      </div>
    );
  }

  return (
    <ProfileContext.Provider value={{ profile, reload }}>
      <BrowserRouter>
        <AppShell>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewScreen />} />
            <Route path="/requests" element={<RequestsScreen />} />
            <Route path="/assign" element={<AssignScreen />} />
            <Route path="/assign/:itemId" element={<AssignScreen />} />
            <Route path="/live" element={<LiveScreen />} />
            <Route path="/team" element={<TeamScreen />} />
            <Route path="/coverage" element={<CoverageScreen />} />
            <Route path="/prices" element={<PricesScreen />} />
            <Route path="/documents" element={<DocumentsScreen />} />
            <Route path="/earnings" element={<EarningsScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </AppShell>
      </BrowserRouter>
    </ProfileContext.Provider>
  );
}
